import axios from "axios";
import envConfig from "../config/environment";
import myAxios from "../Component/AxiosUrl";

const CDN_URL = envConfig.getCdnUrl();
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);
const cdnClient = axios.create({ timeout: 30000 });

cdnClient.interceptors.request.use((requestConfig) => {
  requestConfig.headers = requestConfig.headers || {};
  delete requestConfig.headers.Authorization;
  delete requestConfig.headers.authorization;
  return requestConfig;
});

class UploadService {
  /**
   * Upload image to CDN
   * @param {File} file - File to upload
   * @param {function} onProgress - Progress callback (0-100)
   * @param {string} customFilename - Optional custom filename (e.g., "photo-playerId.png")
   * @returns {Promise<{success: boolean, url?: string, filename?: string, size?: number, error?: string}>}
   */
  async uploadToCDN(file, onProgress = null, customFilename = null) {
    try {
      if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
        return {
          success: false,
          error: "Please select a JPEG, PNG, or WebP image",
        };
      }

      if (file.size > MAX_FILE_SIZE) {
        return { success: false, error: "File size too large. Maximum 5MB allowed" };
      }

      const formData = new FormData();
      formData.append("photo", file);

      // Add custom filename if provided
      if (customFilename && customFilename.trim() !== "") {
        formData.append("filename", customFilename.trim());
      }

      const tokenResponse = await myAxios.post("/upload/token", {
        type: customFilename ? "admin" : "general",
        maxUploads: 1,
        expiryMinutes: 5,
      });
      const uploadToken = tokenResponse.data?.data?.token;
      if (!uploadToken) {
        throw new Error("Could not authorize image upload");
      }

      const response = await cdnClient.post(`${CDN_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-upload-token": uploadToken,
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });

      if (response.data.success) {
        return {
          success: true,
          url: response.data.url,
          filename: response.data.filename,
          size: response.data.size,
        };
      }

      return { success: false, error: response.data.error || "Upload failed" };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || "Upload failed",
      };
    }
  }

  async uploadMultipleImages(files, onProgress = null) {
    try {
      const uploadPromises = Array.from(files).map((file, index) =>
        this.uploadToCDN(file, (progress) => {
          if (onProgress) onProgress(index, progress);
        })
      );

      const results = await Promise.all(uploadPromises);
      const successUploads = results.filter((r) => r.success);
      const failedUploads = results.filter((r) => !r.success);

      return {
        success: failedUploads.length === 0,
        uploaded: successUploads.length,
        failed: failedUploads.length,
        results,
        urls: successUploads.map((r) => r.url),
      };
    } catch (error) {
      return { success: false, error: error.message || "Multiple upload failed" };
    }
  }

  async uploadImageFile(event, onProgress = null) {
    if (!event.target.files || event.target.files.length === 0) {
      return { success: false, error: "No file selected" };
    }
    return this.uploadToCDN(event.target.files[0], onProgress);
  }

  getCDNUrl() {
    return CDN_URL;
  }

  /**
   * Generate standardized CDN filename
   * @param {string} type - Entity type (player, profile, team, league)
   * @param {string} id - Entity ID
   * @param {string} extension - File extension (default: png)
   * @returns {string} Standardized filename
   */
  generateFilename(type, id, extension = "png") {
    const prefixes = {
      player: "photo",
      profile: "profile",
      team: "logo",
      league: "league",
    };
    const prefix = prefixes[type] || type;
    return `${prefix}-${id}.${extension}`;
  }

  /**
   * Upload with standardized filename
   * @param {File} file - File to upload
   * @param {string} type - Entity type (player, profile, team, league)
   * @param {string} id - Entity ID
   * @param {function} onProgress - Progress callback
   * @returns {Promise} Upload result
   */
  async uploadWithStandardName(file, type, id, onProgress = null) {
    const ext = file.name.split(".").pop() || "png";
    const filename = this.generateFilename(type, id, ext);
    return this.uploadToCDN(file, onProgress, filename);
  }
}

const uploadService = new UploadService();

export default uploadService;
